/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
import * as React from 'react';

function makeChainable(validate) {
  function checkType(isRequired, props, propName, componentName, location) {
    componentName = componentName || 'ANONYMOUS';

    if (props[propName] == null) {
      if (isRequired) {
        return new Error(`Required prop \`${propName}\` was not specified in ` + `\`${componentName}\`.`);
      }

      return null;
    }

    return validate(props, propName, componentName, location);
  }

  const chainedCheckType = checkType.bind(null, false);
  chainedCheckType.isRequired = checkType.bind(null, true);
  return chainedCheckType;
}

function isElementOfType(element, type) {
  let result = false;

  if (Array.isArray(type)) {
    result = type.indexOf(element.type) !== -1;
  } else {
    result = element.type === type;
  }

  return result;
}

function getTypeDesc(type) {
  let desc = '(Unknown)';

  if (typeof type === 'string') {
    desc = type;
  } else if (type && type.displayName) {
    desc = type.displayName;
  } else if (Array.isArray(type)) {
    desc = type.map(t => getTypeDesc(t)).join(', ');
  }

  return desc;
}

function createDOMElementChecker() {
  return makeChainable((props, propName, componentName) => {
    const value = props[propName];

    if (!(value && value.nodeType === 1)) {
      return new Error(`Invalid prop \`${propName}\` supplied to ${componentName}, expected ` + `a DOM Element, received \`${typeof value}\``);
    }

    return null;
  });
}

function createuint32Checker() {
  return makeChainable((props, propName, componentName) => {
    const value = props[propName];

    if (value !== value >>> 0) {
      return new Error(`Invalid prop \`${propName}\` supplied to ${componentName}, expected ` + `a uint32, received \`${value}\``);
    }

    return null;
  });
}

function createNonEmptyStringChecker() {
  return makeChainable((props, propName, componentName) => {
    const value = props[propName];

    if (!(typeof value === 'string' && value.length > 0)) {
      return new Error(`Invalid prop \`${propName}\` supplied to ${componentName}, expected ` + `a non-empty string, received \`${value}\``);
    }

    return null;
  });
}

function createForbiddenChecker() {
  return makeChainable((props, propName, componentName) => {
    if (Object.prototype.hasOwnProperty.call(props, propName)) {
      return new Error(`Forbidden prop \`${propName}\` supplied to ${componentName}`);
    }

    return null;
  });
}

function createElementOfTypeChecker(types) {
  const validate = function validate(props, propName, componentName) {
    if (!props) return null;
    const value = props[propName];

    if (! /*#__PURE__*/React.isValidElement(value)) {
      return new Error(`Invalid prop \`${propName}\` supplied to ${componentName}, expected ` + 'single React element, received invalid or multiple React element(s)');
    }

    if (!isElementOfType(value, types)) {
      return new Error(`Invalid prop \`${propName}\` supplied to ${componentName}, expected ` + `element of type ${getTypeDesc(types)}, received \`${value.type}\``);
    }

    return null;
  };

  return validate;
}

function createElementsOfTypeChecker(types) {
  const validate = function validate(props, propName, componentName) {
    if (!props) return null;
    let error = null;
    React.Children.forEach(props[propName], child => {
      if (error) return;

      if (! /*#__PURE__*/React.isValidElement(child)) {
        error = new Error(`Invalid prop \`${propName}\` supplied to ${componentName}, expected ` + `elements of type ${getTypeDesc(types)}, received invalid React element(s)`);
        return;
      }

      if (!isElementOfType(child, types)) {
        error = new Error(`Invalid prop \`${propName}\` supplied to ${componentName}, expected ` + `elements of type ${getTypeDesc(types)}, received \`${child.type}\``);
      }
    });
    return error;
  };

  return validate;
}

export default {
  DOMElement: createDOMElementChecker(),
  ElementOfType: createElementOfTypeChecker,
  ElementsOfType: createElementsOfTypeChecker,
  Forbidden: createForbiddenChecker(),
  NonEmptyString: createNonEmptyStringChecker(),
  uint32: createuint32Checker()
};